import * as yup from '@if/core/yup'
import { ControlledChipsField, useFormDialog, useSnackbar } from '@if/mui'
import { useForceTransferVendors } from '@src/api/failed-vendors-api'
import { MERCHANT_ID, normalizeMerchantId } from '@src/services/merchant-id-parser'
import { ForceTransferVendorResult } from '@src/types/failed-vendor-sync'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const MAX_MERCHANTS = 100

const buildSchema = (invalidIdMessage: string, duplicateIdMessage: string) =>
  yup.object({
    merchantIds: yup
      .array()
      .of(yup.text().required().matches(MERCHANT_ID, invalidIdMessage))
      .required()
      .min(1)
      .max(MAX_MERCHANTS)
      .unique((merchantId: string) => normalizeMerchantId(merchantId), duplicateIdMessage)
      .default([]),
  })

type MerchantIdsForm = yup.InferSubmit<ReturnType<typeof buildSchema>>

export const useForceTransferVendorsDialog = (companyId: string) => {
  const { t } = useTranslation()
  const [forceTransferVendors] = useForceTransferVendors(companyId)
  const { showSnackbar } = useSnackbar()

  const schema = useMemo(
    () =>
      buildSchema(
        t('admin.transactionRetry.forceTransfer.errors.invalidId'),
        t('admin.transactionRetry.forceTransfer.errors.duplicateId'),
      ),
    [t],
  )
  const confirmLabel = useMemo(() => ({ create: t('admin.transactionRetry.forceTransfer.actions.launch') }), [t])

  return useFormDialog<MerchantIdsForm, void, ForceTransferVendorResult[]>({
    title: t('admin.transactionRetry.forceTransfer.sectionTitle'),
    confirmLabel,
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    defaultValues: { merchantIds: [] },
    successMessage: (results) =>
      t('admin.transactionRetry.forceTransfer.messages.success', { transferred: results.length }),
    content: ({ form }) => (
      <ControlledChipsField
        control={form.control}
        trigger={form.trigger}
        name="merchantIds"
        label={t('admin.transactionRetry.forceTransfer.labels.merchantIds')}
        placeholder={t('admin.transactionRetry.forceTransfer.labels.placeholder')}
        helperText={t('admin.transactionRetry.forceTransfer.labels.chipsHelper')}
        max={MAX_MERCHANTS}
        required
        multiline
        minRows={4}
        maxRows={8}
      />
    ),
    onSubmit: async (values) => {
      let results: ForceTransferVendorResult[]
      try {
        results = await forceTransferVendors(values.merchantIds)
      } catch (_error) {
        throw new Error(t('admin.transactionRetry.forceTransfer.errors.serviceUnavailable'))
      }

      const failures = results.filter((result) => !result.success).map((result) => result.merchantId)
      if (failures.length === 0) return results

      const transferred = results.length - failures.length
      showSnackbar(
        t('admin.transactionRetry.forceTransfer.messages.partial', {
          transferred,
          failed: failures.length,
          merchants: failures.join(', '),
        }),
        { severity: transferred > 0 ? 'warning' : 'error' },
      )
      return false
    },
  })
}
